import React from 'react';
import { Link } from 'react-router-dom';
import { COMPANY_INFO } from '../data/mock';

const TermsPage = () => {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#F8FAFC] to-white py-10 md:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#111111]">
            Terms & Conditions
          </h1>
          <p className="mt-4 text-[#475569]">
            General Terms & Conditions, Service-Specific Terms & Privacy Policy
          </p>
          <p className="mt-2 text-sm text-[#64748B]">
            Last updated: January 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-10 md:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none text-[#475569]">
            
            {/* Quick Links */}
            <div className="bg-[#F8FAFC] rounded-xl p-6 mb-10 not-prose">
              <h3 className="font-semibold text-[#111111] mb-4">Quick Navigation</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#part-a" className="text-[#0D9488] hover:underline">Part A — General Terms & Conditions</a></li>
                <li><a href="#part-b" className="text-[#0D9488] hover:underline">Part B — Service-Specific Terms</a></li>
                <li className="pl-4"><a href="#battery-removal" className="text-[#7C3AED] hover:underline">→ B4. Battery Installation Packaging & Waste Collection</a></li>
                <li><a href="#part-c" className="text-[#0D9488] hover:underline">Part C — Privacy Policy</a></li>
              </ul>
            </div>

            {/* PART A */}
            <section id="part-a">
              <h2 className="text-2xl font-bold text-[#111111] border-b border-[#E2E8F0] pb-4 mb-6">
                PART A — GENERAL TERMS & CONDITIONS
              </h2>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">1. About Transa</h3>
              <p>
                "Transa", "we", "us", or "our" refers to <strong>The Trustee for Keystone Heritage Trust trading as Transa</strong> (ABN 83 164 418 663), operating as a logistics and 
                reverse-logistics service provider.
              </p>
              <p>
                Transa provides <strong>installer-focused site logistics services</strong>, including packaging 
                recovery and related pickup services.
              </p>
              <p>
                Transa's standard pickup services are primarily available within the service areas published on the 
                Transa website. Services outside the standard service areas may be available by agreement and may incur 
                additional charges.
              </p>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">2. Acceptance of Terms</h3>
              <p>By:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>booking a pickup,</li>
                <li>subscribing to any Transa service,</li>
                <li>requesting pricing, or</li>
                <li>otherwise using Transa's services,</li>
              </ul>
              <p className="mt-4">
                you ("you", "the customer", "the installer") agree to be bound by these Terms & Conditions, 
                including the Service-Specific Terms set out in Part B.
              </p>
              <p><strong>If you do not agree, you must not use Transa's services.</strong></p>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">3. Scope of Services</h3>
              <p>Transa provides <strong>logistics and reverse-logistics services</strong>, which may include:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>collection of installation packaging;</li>
                <li>removal of cartons, foam inserts, plastic wrapping, and related non-hazardous packaging;</li>
                <li>scheduled or priority pickups based on the selected service model.</li>
              </ul>
              <p className="mt-4">
                Transa <strong>does not provide waste processing, demolition, site cleanup, sorting services, or 
                hazardous-waste services</strong>, unless expressly agreed in writing.
              </p>
              <p className="mt-4">
                Transa may determine the appropriate handling, consolidation, recycling pathway, disposal pathway, 
                or transfer destination for collected materials, subject to applicable laws.
              </p>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">4. Service Area</h3>
              <p>
                Transa's standard pickup services are available within the service areas <strong>published on the 
                Transa website</strong>, unless otherwise agreed in writing.
              </p>
              <p>Customers and installers can check the current service areas at:</p>
              <p>
                <a href="https://transa.com.au/service-area" className="text-[#0D9488] hover:underline">
                  https://transa.com.au/service-area
                </a>
              </p>
              <p className="mt-4">
                Transa may accept pickup bookings outside the standard service areas at its discretion. This may apply 
                to all pickup options, including Standard Pickup, Priority Pickup, and Subscription Service pickups.
              </p>
              <p className="mt-4">
                Where a pickup location is outside Transa's standard service area, additional travel, distance, 
                location-based, or service-related charges may apply. Any applicable additional charges will be 
                forwarded to the client or customer based on the pickup location, distance of travel, and service 
                requirements.
              </p>
              <p>Transa reserves the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>decline bookings outside the service area;</li>
                <li>apply additional charges for pickups outside the service area;</li>
                <li>cancel, redirect, or reschedule bookings incorrectly submitted as being within the service area; or</li>
                <li>request further information before confirming an outside-area pickup.</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">5. Booking & Scheduling</h3>
              <p><strong>5.1 Booking Requests</strong></p>
              <p>All bookings are requests only and are subject to confirmation by Transa via SMS, email, or phone.</p>
              
              <p className="mt-4"><strong>5.2 Pickup Timeframes</strong></p>
              <p>Pickup timeframes depend on the selected service model (Standard, Subscription, or Priority). Timeframes are indicative and may be affected by:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>access constraints;</li>
                <li>weather;</li>
                <li>safety considerations;</li>
                <li>traffic or operational delays;</li>
                <li>incorrect booking information;</li>
                <li>materials not being ready for pickup.</li>
              </ul>

              <p className="mt-4"><strong>5.3 Access Requirements</strong></p>
              <p>You must ensure:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>safe and legal access to the pickup location;</li>
                <li>packaging is safely stacked and accessible;</li>
                <li>no obstruction to vehicles or personnel;</li>
                <li>materials are ready for collection before Transa arrives.</li>
              </ul>
              <p className="mt-2">Failed access may result in additional fees or rescheduling.</p>

              <p className="mt-4"><strong>5.4 Photos & Readiness Checks</strong></p>
              <p>Transa may request photos of the materials before confirming or attending a pickup.</p>
              <p className="mt-2">
                Photos may be used to assess readiness, estimate volume, confirm service scope, and determine whether 
                additional charges may apply.
              </p>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">6. What Transa Will Collect</h3>
              <p>Unless otherwise agreed, Transa will collect:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>cardboard packaging;</li>
                <li>thermo-foam or protective inserts;</li>
                <li>plastic wrapping and internal packaging;</li>
                <li>packaging associated with inverters, BMS units, batteries, and balance-of-system components.</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">7. What Transa Will NOT Collect</h3>
              <p>Transa does <strong>not</strong> collect:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>hazardous or regulated waste;</li>
                <li>liquids, chemicals, or contaminated materials;</li>
                <li>asbestos or building demolition waste;</li>
                <li>general household or unrelated construction waste;</li>
                <li>pallets or oversized items unless pre-approved.</li>
              </ul>
              <p className="mt-2">If prohibited materials are presented, Transa may refuse collection and charge a call-out fee, cancellation fee, rescheduling fee, or additional handling fee where applicable.</p>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">8. Customer Responsibilities</h3>
              <p>You are responsible for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>ensuring materials presented match the booking description;</li>
                <li>complying with all applicable laws and site rules;</li>
                <li>ensuring materials are safe to handle;</li>
                <li>confirming ownership or authority to dispose of the materials;</li>
                <li>ensuring the materials are sorted, prepared, and accessible before Transa arrives.</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">9. Fees & Payment</h3>
              <p><strong>9.1 Pricing</strong></p>
              <p>Pricing depends on the service model, volume, urgency, location, and the actual materials presented for collection.</p>
              <p className="mt-2">Pricing will be confirmed prior to service delivery wherever possible.</p>
              <p className="mt-2">Additional charges may apply after service delivery if the actual materials, site conditions, access requirements, preparation level, collection time, or service scope differ from the booking details.</p>
              
              <p className="mt-4"><strong>9.2 Payment Terms</strong></p>
              <p>Unless otherwise agreed:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>invoices are payable within the stated payment period;</li>
                <li>late payments may incur administrative fees;</li>
                <li>Transa may suspend services for overdue accounts.</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">10. Cancellations, Changes & Failed Pickups</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Bookings cancelled within 24 hours of the scheduled pickup may incur a fee.</li>
                <li>Priority bookings may have stricter cancellation terms (see Part B).</li>
                <li>Changes must be requested as early as possible.</li>
              </ul>
              <p className="mt-4">
                If Transa attends the site and the materials are not ready, inaccessible, unsafe, unsorted, outside 
                the booked scope, or include prohibited materials, Transa may treat the attendance as a failed pickup 
                and charge a call-out fee, cancellation fee, rescheduling fee, or additional handling fee.
              </p>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">11. Risk & Liability</h3>
              <p><strong>11.1 General Limitation</strong></p>
              <p>To the maximum extent permitted by law, Transa is not liable for indirect, incidental, or consequential losses, including lost profits or downtime.</p>
              
              <p className="mt-4"><strong>11.2 Damage</strong></p>
              <p>Transa will take reasonable care but is not responsible for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>pre-existing site damage;</li>
                <li>damage caused by unsafe stacking or access;</li>
                <li>materials incorrectly described;</li>
                <li>materials placed in unsafe, obstructive, unstable, or unsuitable locations.</li>
              </ul>
              
              <p className="mt-4"><strong>11.3 Australian Consumer Law</strong></p>
              <p>Nothing in these Terms excludes any rights you may have under the Australian Consumer Law.</p>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">12. Insurance</h3>
              <p>Transa maintains appropriate business and vehicle insurance relevant to its operations.</p>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">13. Force Majeure</h3>
              <p>Transa is not liable for delays or failure caused by events beyond reasonable control, including weather, accidents, road closures, traffic delays, operational disruptions, or government actions.</p>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">14. Termination</h3>
              <p>Transa may suspend or terminate services if:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>these Terms are breached;</li>
                <li>unsafe or prohibited materials are presented;</li>
                <li>payment obligations are not met;</li>
                <li>repeated failed pickups or access issues occur.</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">15. Governing Law</h3>
              <p>These Terms are governed by the laws of <strong>Victoria, Australia</strong>.</p>
            </section>

            {/* PART B */}
            <section id="part-b" className="mt-16">
              <h2 className="text-2xl font-bold text-[#111111] border-b border-[#E2E8F0] pb-4 mb-6">
                PART B — SERVICE-SPECIFIC TERMS
              </h2>

              <div className="bg-[#F8FAFC] rounded-xl p-6 mb-8">
                <h3 className="text-xl font-semibold text-[#111111] mb-4">B1. Standard Pickup Service</h3>
                
                <p className="font-medium text-[#111111] mt-4">Service Description</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Pay-per-job pickup</li>
                  <li>Collection <strong>within 7 days</strong> of confirmed booking</li>
                </ul>

                <p className="font-medium text-[#111111] mt-4">Key Conditions</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>No guaranteed pickup day or time within the 7-day window</li>
                  <li>Suitable for non-urgent jobs</li>
                  <li>Pricing confirmed per booking</li>
                  <li>Materials must be ready and accessible before pickup</li>
                </ul>

                <p className="font-medium text-[#111111] mt-4">Cancellations</p>
                <p className="mt-2">Cancellation within 24 hours of scheduled pickup may incur a fee.</p>
                <p className="mt-2">If Transa attends the site and the materials are not ready, inaccessible, unsafe, unsorted, outside the booked scope, or include prohibited materials, the attendance may be treated as a failed pickup and additional charges may apply.</p>
              </div>

              <div className="bg-[#F0FDFA] rounded-xl p-6 mb-8 border border-[#0D9488]/20">
                <h3 className="text-xl font-semibold text-[#111111] mb-4">B2. Subscription Service</h3>
                
                <p className="font-medium text-[#111111] mt-4">Service Description</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Discounted pricing for regular installers</li>
                  <li>Same service level as Standard Pickup</li>
                  <li>Pickup <strong>within 7 days</strong> of booking</li>
                  <li>Typically includes up to <strong>5 jobs per week</strong> (unless otherwise agreed)</li>
                </ul>

                <p className="font-medium text-[#111111] mt-4">Key Conditions</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Subscription does <strong>not</strong> provide faster pickup than Standard Pickup</li>
                  <li>Excess jobs may be charged per job, or redirected to Priority Pickup, depending on availability</li>
                  <li>Materials must be ready and accessible before pickup</li>
                  <li>Unused weekly pickups do not roll over unless expressly agreed by Transa in writing</li>
                </ul>

                <p className="font-medium text-[#111111] mt-4">Billing</p>
                <p className="mt-2">Subscription services are billed weekly or monthly, as agreed.</p>
                <p className="mt-2">Each Subscription package has a minimum term of <strong>thirteen (13) weeks</strong>.</p>

                <p className="font-medium text-[#111111] mt-4">Subscription Term & Cancellation</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Each Subscription package has a minimum term of <strong>thirteen (13) weeks</strong>.</li>
                  <li>At the end of each 13-week subscription term, the Subscription will automatically renew for a further thirteen (13) week term unless the client provides written notice of cancellation before the end of the current 13-week term.</li>
                  <li>Cancellation requests must be made in writing and must relate to the specific Subscription package the client wishes to cancel.</li>
                  <li>If a cancellation request is not received before the end of the current 13-week term, the Subscription will automatically renew and the client will continue on that Subscription for a further thirteen (13) weeks.</li>
                  <li>Unused pickups, credits, or service allowances do not roll over or convert to cash unless expressly agreed by Transa in writing.</li>
                </ul>

                <p className="font-medium text-[#111111] mt-4">Subscription Pickup Cancellations</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Subscription clients may cancel or reschedule a booked pickup by giving at least 24 hours' notice before the scheduled pickup time.</li>
                  <li>Cancellations made within 24 hours of the scheduled pickup may be counted as one of the client's included weekly pickups or may incur a cancellation fee.</li>
                  <li>If Transa attends the site and the materials are not ready, inaccessible, unsafe, unsorted, outside the booked scope, or include prohibited materials, the attendance may be treated as a completed pickup or failed pickup, and additional charges may apply.</li>
                </ul>

                <p className="font-medium text-[#111111] mt-4">Fair Use</p>
                <p className="mt-2">Transa reserves the right to review usage patterns to ensure fair and reasonable use of subscription plans.</p>
                <p className="mt-2">Where usage exceeds the agreed Subscription allowance or materially differs from the intended use of the Subscription package, Transa may apply additional charges, require the client to move to a different package, or redirect excess jobs to Priority Pickup.</p>
              </div>

              <div className="bg-[#FEF3C7] rounded-xl p-6 mb-8 border border-[#D97706]/20">
                <h3 className="text-xl font-semibold text-[#111111] mb-4">B3. Priority Pickup Service (48 Hours)</h3>
                
                <p className="font-medium text-[#111111] mt-4">Service Description</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Pickup targeted <strong>within 48 hours</strong> of booking confirmation</li>
                  <li>Premium service designed for urgent or high-volume periods</li>
                </ul>

                <p className="font-medium text-[#111111] mt-4">Key Conditions</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Priority scheduling takes precedence over Standard bookings</li>
                  <li>Availability may be limited during peak periods</li>
                  <li>Priority Pickup is subject to confirmation by Transa</li>
                  <li>Materials must be ready and accessible before pickup</li>
                </ul>

                <p className="font-medium text-[#111111] mt-4">Cancellations</p>
                <p className="mt-2">Cancellations within 24 hours of booking may be fully chargeable.</p>
                <p className="mt-2">If Transa attends the site and the materials are not ready, inaccessible, unsafe, unsorted, outside the booked scope, or include prohibited materials, the attendance may be treated as a failed pickup and additional charges may apply.</p>
              </div>

              {/* B4 - Battery Installation Packaging & Waste Collection */}
              <div id="battery-removal" className="bg-[#EDE9FE] rounded-xl p-6 mb-8 border border-[#7C3AED]/20">
                <h3 className="text-xl font-semibold text-[#111111] mb-4">B4. Battery Installation Packaging & Waste Collection — Service Conditions & Requirements</h3>
                
                <p className="font-medium text-[#111111] mt-4">1. Purpose of the Service</p>
                <p className="mt-2">
                  This service is provided by Transa to support residential battery installations by collecting 
                  installation-related packaging and non-hazardous waste efficiently and safely.
                </p>
                <div className="bg-white/50 rounded-lg p-4 mt-3">
                  <p className="font-semibold text-[#7C3AED]">Important:</p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>Transa provides <strong>collection of pre-sorted installation packaging and related non-hazardous waste</strong>.</li>
                    <li>Transa does <strong>not</strong> provide on-site sorting, site cleanup, demolition waste removal, general rubbish removal, or hazardous waste removal.</li>
                    <li>The service is designed for quick pickup of pre-sorted materials, not on-site cleanup or sorting.</li>
                  </ul>
                </div>

                <p className="font-medium text-[#111111] mt-6">2. Service Scope</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>This service applies to <strong>one (1) residential battery installation only</strong>.</li>
                  <li>All waste must originate from the same site as the battery installation.</li>
                  <li>Waste from other jobs, sites, or installations must not be added.</li>
                  <li>This is not a general rubbish removal service.</li>
                  <li>Transa reserves the right to refuse collection or apply additional charges if the scope is exceeded.</li>
                </ul>

                <p className="font-medium text-[#111111] mt-6">3. Installer Responsibilities — Waste Sorting & Preparation</p>
                <p className="mt-2">All waste must be sorted and prepared by the installer before collection.</p>
                
                <div className="bg-white/50 rounded-lg p-4 mt-3">
                  <p className="font-semibold text-[#111111]">Required Waste Sorting (Mandatory):</p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>
                      <strong>Recyclable cardboard</strong> — Stacked neatly together
                    </li>
                    <li>
                      <strong>Large packaging</strong> — Foam, polystyrene, and bulky packaging grouped together
                    </li>
                    <li>
                      <strong>Small loose waste</strong> — Cable off-cuts, wire trimmings, cable ties, plastics, small metal pieces
                      <br /><em className="text-[#64748B]">Must be placed together in one plastic bag or suitable container</em>
                    </li>
                  </ul>
                  <p className="mt-3 text-[#DC2626] font-medium">Mixed, scattered, or unsorted waste is not considered ready for pickup.</p>
                </div>

                <p className="font-medium text-[#111111] mt-6">4. Waste Placement & Site Conditions</p>
                <p className="mt-2">Before Transa arrives, waste must be:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Neatly stacked, compact, and ready to lift</li>
                  <li>Placed in a safe location within the customer's property</li>
                  <li>Positioned away from public access to prevent interference</li>
                  <li>Protected from wind and rain where reasonably possible
                    <ul className="list-disc pl-6 mt-1 text-[#64748B]">
                      <li>Wind may scatter light materials</li>
                      <li>Rain may damage or weigh down cardboard</li>
                    </ul>
                  </li>
                </ul>
                <p className="mt-3 text-[#DC2626]">Waste must not be spread across the site or mixed together.</p>

                <p className="font-medium text-[#111111] mt-6">5. Time Allowance & Additional Charges</p>
                <p className="mt-2">Each booking includes <strong>10 minutes of on-site collection time only</strong>.</p>
                <p className="mt-2">The 10-minute allowance assumes:</p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Waste is fully sorted</li>
                  <li>Waste is neatly staged</li>
                  <li>No on-site separation or cleanup is required</li>
                  <li>The materials are easily accessible and ready to lift</li>
                </ul>
                
                <div className="bg-[#FEF2F2] rounded-lg p-4 mt-4 border border-[#DC2626]/20">
                  <p className="font-semibold text-[#DC2626]">Additional Labour Charges</p>
                  <p className="mt-2">If Transa is required to:</p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>Sort mixed waste</li>
                    <li>Separate materials</li>
                    <li>Gather scattered rubbish</li>
                    <li>Clean up untidy or unsecured waste</li>
                    <li>Spend additional time due to poor access or poor preparation</li>
                  </ul>
                  <p className="mt-3 font-semibold">Then additional labour time will apply and will be charged at:</p>
                  <p className="text-xl font-bold text-[#DC2626] mt-2">$20 per additional 10 minutes (or part thereof)</p>
                </div>

                <p className="font-medium text-[#111111] mt-6">6. Prohibited Materials (Strictly Not Accepted)</p>
                <p className="mt-2">The following materials must <strong>not</strong> be included under any circumstances:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2 text-[#DC2626]">
                  <li>Hazardous materials</li>
                  <li>Flammable materials</li>
                  <li>Fuel, oils, or chemicals</li>
                  <li>Gas cylinders or pressurised containers</li>
                  <li>Asbestos or asbestos-containing materials</li>
                  <li>Any waste requiring regulated or specialist disposal</li>
                </ul>
                <p className="mt-3 font-medium">
                  If prohibited materials are present, Transa may refuse collection, cancel the booking, retain any 
                  prepaid amount, and/or apply additional charges where applicable.
                </p>
                <p className="mt-2">Transa may report the issue to relevant authorities if required by law.</p>

                <p className="font-medium text-[#111111] mt-6">7. Right to Refuse Service</p>
                <p className="mt-2">Transa reserves the right to:</p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Refuse collection</li>
                  <li>Apply additional charges</li>
                  <li>Cancel a booking</li>
                  <li>Treat the attendance as a failed pickup</li>
                  <li>Retain any prepaid amount where applicable</li>
                </ul>
                <p className="mt-2">If these Service Conditions & Requirements are not met.</p>

                <p className="font-medium text-[#111111] mt-6">8. Acceptance of Conditions</p>
                <p className="mt-2">By booking this service, the installer confirms that:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>All waste will be pre-sorted and prepared as required</li>
                  <li>No prohibited materials are included</li>
                  <li>The service is for one residential battery installation only</li>
                  <li>Additional charges apply if on-site sorting, cleanup, additional handling, or additional collection time is required</li>
                </ul>

                <div className="bg-white/50 rounded-lg p-4 mt-6">
                  <p className="font-semibold text-[#111111]">9. Summary</p>
                  <p className="mt-2">This service exists to:</p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>Improve installer efficiency</li>
                    <li>Maintain clean, professional customer sites</li>
                    <li>Enable fast, predictable waste collection</li>
                  </ul>
                  <p className="mt-3 font-medium text-[#7C3AED]">
                    It is not a rubbish sorting, site cleanup, general rubbish removal, or multi-job waste removal service.
                  </p>
                </div>
              </div>
            </section>

            {/* PART C */}
            <section id="part-c" className="mt-16">
              <h2 className="text-2xl font-bold text-[#111111] border-b border-[#E2E8F0] pb-4 mb-6">
                PART C — PRIVACY POLICY
              </h2>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">1. Introduction</h3>
              <p>
                Transa is committed to protecting your privacy and handling personal information in accordance 
                with the <strong>Privacy Act 1988 (Cth)</strong> and the <strong>Australian Privacy Principles (APPs)</strong>.
              </p>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">2. Information We Collect</h3>
              <p>We may collect:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>business name;</li>
                <li>contact name;</li>
                <li>phone number;</li>
                <li>email address;</li>
                <li>pickup address and suburb;</li>
                <li>booking details and service preferences;</li>
                <li>uploaded photos (if provided);</li>
                <li>communications between you and Transa.</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">3. How We Collect Information</h3>
              <p>Information is collected when you:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>submit forms on our website;</li>
                <li>book a pickup;</li>
                <li>contact us by phone, email, or messaging;</li>
                <li>enter into a subscription arrangement;</li>
                <li>provide photos or other service-related information.</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">4. How We Use Information</h3>
              <p>We use your information to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>provide and manage services;</li>
                <li>schedule pickups;</li>
                <li>communicate with you;</li>
                <li>issue invoices;</li>
                <li>assess pickup readiness;</li>
                <li>confirm service scope and pricing;</li>
                <li>improve our services;</li>
                <li>comply with legal obligations.</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">5. Disclosure of Information</h3>
              <p>We may disclose information to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>employees or contractors assisting with service delivery;</li>
                <li>IT and service providers;</li>
                <li>regulators or authorities where required by law.</li>
              </ul>
              <p className="mt-2"><strong>We do not sell personal information.</strong></p>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">6. Data Storage & Security</h3>
              <p>We take reasonable steps to protect personal information from misuse, loss, and unauthorised access.</p>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">7. Access & Correction</h3>
              <p>You may request access to or correction of your personal information by contacting Transa.</p>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">8. Cookies & Website Analytics</h3>
              <p>Our website may use cookies or analytics tools to improve functionality and user experience.</p>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">9. Complaints</h3>
              <p>Privacy complaints may be directed to Transa.</p>
              <p className="mt-2">
                If unresolved, complaints may be escalated to the Office of the Australian Information Commissioner (OAIC).
              </p>

              <h3 className="text-xl font-semibold text-[#111111] mt-8 mb-4">10. Changes to this Policy</h3>
              <p>We may update these Terms and the Privacy Policy from time to time.</p>
              <p className="mt-2">The latest version will be published on the Transa website.</p>
            </section>

            {/* Footer Note */}
            <div className="mt-16 pt-8 border-t border-[#E2E8F0]">
              <p className="text-center text-[#111111] font-semibold">
                TRANSA.COM.AU
              </p>
              <p className="text-center text-[#64748B] mt-2">
                Installer-focused logistics. Clear. Simple. Professional.
              </p>
              <p className="text-center text-sm text-[#94A3B8] mt-4">
                Transa is a registered business name of the Trustee for Keystone Heritage Trust (ABN 83 164 418 663)
              </p>
            </div>

          </div>
        </div>
      </section>
    </main>
  );
};

export default TermsPage;
